import { createTheme } from '@mui/material/styles';

// PSI Color Tokens
export const psiTokens = {
  // Primary gradient colors
  primary: {
    dark: '#7b3080',
    main: '#9873AC',
    light: '#a76fcc',
  },
  // Text colors
  text: {
    primary: '#1a1721',    // ink
    secondary: '#383342',   // body
    tertiary: '#706978',    // muted
  },
  // Background & surface
  background: {
    primary: '#ffffff',
    secondary: '#f5f4f7',
    tertiary: '#f9f8fa',
  },
  // Border & dividers
  border: '#dcdae0',
  borderLight: '#e4dded',

  // State colors
  success: '#2e7d32',
  warning: '#e65100',
  error: '#b71c1c',

  // Credential accent colors — see "Diplomas vs Certificates" in CLAUDE.md.
  // Certificates use the site's normal vibrant purple (primary); diplomas
  // use this deliberately less-saturated shade so the two credential types
  // are visually distinguishable at a glance, certificates read as the
  // more significant achievement.
  credential: {
    certificate: '#7b3080', // == primary.dark, vibrant
    diploma: '#8a7d99', // muted, same tone already used for the footer credit line
  },
};

// Theme options with Material UI
const themeOptions = {
  palette: {
    primary: {
      main: psiTokens.primary.main,
      dark: psiTokens.primary.dark,
      light: psiTokens.primary.light,
    },
    secondary: {
      main: psiTokens.primary.light,
    },
    background: {
      default: psiTokens.background.primary,
      paper: psiTokens.background.secondary,
    },
    text: {
      primary: psiTokens.text.primary,
      secondary: psiTokens.text.secondary,
    },
    success: {
      main: psiTokens.success,
    },
    warning: {
      main: psiTokens.warning,
    },
    error: {
      main: psiTokens.error,
    },
    divider: psiTokens.border,
  },
  typography: {
    fontFamily: '"Titillium Web", "Roboto", "Helvetica", "Arial", sans-serif',

    // Headings
    h1: {
      fontSize: 'clamp(36px, 6vw, 58px)',
      fontWeight: 900,
      lineHeight: 1.08,
      letterSpacing: '-0.01em',
    },
    h2: {
      fontSize: 'clamp(34px, 5.5vw, 46px)',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.005em',
    },
    h3: {
      fontSize: 'clamp(30px, 4.5vw, 40px)',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: 1.35,
    },
    h5: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.5,
    },

    // Body text
    body1: {
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: 1.65,
      letterSpacing: '0.005em',
    },
    body2: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.6,
    },

    // Subtitle
    subtitle1: {
      fontSize: '15px',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    subtitle2: {
      fontSize: '13px',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },

    // Button & labels
    button: {
      fontSize: '12px',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
    caption: {
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
    },
    overline: {
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          textTransform: 'uppercase',
          fontWeight: 700,
          fontSize: '12px',
          letterSpacing: '0.1em',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(90deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
          color: '#ffffff',
          '&:hover': {
            background: `linear-gradient(90deg, #5c2e50, ${psiTokens.primary.light})`,
          },
        },
        outlinedPrimary: {
          borderColor: psiTokens.primary.light,
          color: psiTokens.primary.light,
          '&:hover': {
            backgroundColor: 'rgba(167, 111, 204, 0.04)',
            borderColor: psiTokens.primary.light,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: `1px solid ${psiTokens.border}`,
          backgroundColor: psiTokens.background.secondary,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: psiTokens.background.secondary,
            borderRadius: '8px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
};

export const psiTheme = createTheme(themeOptions);

// Shared light-contrast button style: used for secondary "contact" style CTAs
// that sit on top of the purple gradient banners, so they read as a soft,
// legible accent rather than a jarring pure-white/heavy-shadow button.
export const lightButtonSx = {
  // `background` (not `backgroundColor`) is required here: the theme's
  // containedPrimary override sets a `background: linear-gradient(...)`
  // shorthand, and only the shorthand form can fully replace it —
  // `backgroundColor` alone leaves the old background-image showing through.
  background: '#f6f0fa',
  color: psiTokens.primary.dark,
  border: `1.5px solid ${psiTokens.primary.light}`,
  boxShadow: '0 2px 8px rgba(123, 48, 128, 0.15)',
  '&:hover': {
    background: '#ecdff5',
    borderColor: psiTokens.primary.dark,
    boxShadow: '0 2px 10px rgba(123, 48, 128, 0.2)',
  },
};
